import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Checkbox,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import { listRoles, createRole, updateRole, deleteRole, seedRoles } from '../../api/rbac';
import { getPermissionsByCategory, PERMISSION_CATEGORIES } from '../../constants/permissions';
import DWLFLoader from '../../components/DWLFLoader';

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
  const [selectedRole, setSelectedRole] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formPermissions, setFormPermissions] = useState([]);
  const [formError, setFormError] = useState('');

  const permissionsByCategory = getPermissionsByCategory();

  const loadRoles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listRoles();
      setRoles(data?.roles || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const handleSeedRoles = async () => {
    setLoading(true);
    try {
      await seedRoles();
      await loadRoles();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to seed roles');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setDialogMode('create');
    setSelectedRole(null);
    setFormName('');
    setFormDescription('');
    setFormPermissions([]);
    setFormError('');
    setDialogOpen(true);
  };

  const openEditDialog = (role) => {
    setDialogMode('edit');
    setSelectedRole(role);
    setFormName(role.name);
    setFormDescription(role.description || '');
    setFormPermissions(role.permissions || []);
    setFormError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedRole(null);
    setFormError('');
  };

  const handlePermissionToggle = (permissionKey) => {
    setFormPermissions((prev) =>
      prev.includes(permissionKey)
        ? prev.filter((p) => p !== permissionKey)
        : [...prev, permissionKey]
    );
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      setFormError('Role name is required');
      return;
    }

    setSaving(true);
    setFormError('');

    try {
      if (dialogMode === 'create') {
        await createRole({
          name: formName.trim().toLowerCase(),
          description: formDescription.trim(),
          permissions: formPermissions,
        });
      } else {
        await updateRole(selectedRole.roleId, {
          name: formName.trim().toLowerCase(),
          description: formDescription.trim(),
          permissions: formPermissions,
        });
      }
      closeDialog();
      await loadRoles();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = (role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setRoleToDelete(null);
  };

  const handleDelete = async () => {
    if (!roleToDelete) return;

    setDeleting(true);
    try {
      await deleteRole(roleToDelete.roleId);
      closeDeleteDialog();
      await loadRoles();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete role');
      closeDeleteDialog();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        background: 'linear-gradient(145deg, var(--dwlf-ink-02), rgba(0,0,0,0.25))',
      }}
    >
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Role Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create and manage roles with their associated permissions.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Seed default roles">
              <Button variant="outlined" size="small" onClick={handleSeedRoles} disabled={loading}>
                Seed Defaults
              </Button>
            </Tooltip>
            <Tooltip title="Create new role">
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={openCreateDialog}
                disabled={loading}
              >
                New Role
              </Button>
            </Tooltip>
            <Tooltip title="Refresh roles">
              <IconButton onClick={loadRoles} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <DWLFLoader size="medium" showText={false} />
          </Box>
        ) : roles.length === 0 ? (
          <Alert severity="info">
            No roles found. Click "Seed Defaults" to create the default admin and user roles.
          </Alert>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Permissions</TableCell>
                  <TableCell>System</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role.roleId}>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography fontWeight={600}>{role.name}</Typography>
                        {role.system && (
                          <Tooltip title="System role - cannot be deleted">
                            <LockIcon fontSize="small" color="action" />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {role.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {(role.permissions || []).map((perm) => (
                          <Chip key={perm} label={perm} size="small" variant="outlined" />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {role.system ? (
                        <Chip label="Yes" size="small" color="info" />
                      ) : (
                        <Chip label="No" size="small" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title="Edit role">
                          <IconButton size="small" onClick={() => openEditDialog(role)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {!role.system && (
                          <Tooltip title="Delete role">
                            <IconButton size="small" color="error" onClick={() => openDeleteDialog(role)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{dialogMode === 'create' ? 'Create New Role' : 'Edit Role'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}

            <TextField
              label="Role Name"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              fullWidth
              required
              disabled={selectedRole?.system && dialogMode === 'edit'}
              helperText={selectedRole?.system ? 'System role names cannot be changed' : 'Use lowercase letters only'}
            />

            <TextField
              label="Description"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />

            <Divider />

            <Typography variant="subtitle1" fontWeight={600}>
              Permissions
            </Typography>

            {Object.entries(permissionsByCategory).map(([category, permissions]) => (
              <Box key={category}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ textTransform: 'capitalize' }}>
                  {category}
                </Typography>
                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0}>
                  {permissions.map((perm) => (
                    <FormControlLabel
                      key={perm.key}
                      control={
                        <Checkbox
                          checked={formPermissions.includes(perm.key)}
                          onChange={() => handlePermissionToggle(perm.key)}
                          size="small"
                        />
                      }
                      label={
                        <Tooltip title={perm.description}>
                          <Typography variant="body2">{perm.key}</Typography>
                        </Tooltip>
                      }
                      sx={{ minWidth: '200px' }}
                    />
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <DWLFLoader size="tiny" showText={false} /> : dialogMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the role "{roleToDelete?.name}"? This action cannot be undone.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Users with this role will lose its permissions.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <DWLFLoader size="tiny" showText={false} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default RoleManagement;
